import React, { memo, useMemo } from 'react';
import { Target, CheckCircle2, Circle, Plus, Calendar } from 'lucide-react';
import SmartText from '../SmartText';
import { PriorityChip } from '../tasks/TaskFieldChips';
import { dueBucket, formatDue } from '../tasks/taskDates';
import { isDemoted } from '../tasks/taskMeta';
import { PaneShell, PaneEmpty, PaneLink } from './PaneShell';
import { cn } from '../../lib/utils';

/**
 * NowPane — what to do next.
 *
 * Merges what used to be two separate widgets: a "Current Focus" strip that
 * showed exactly one task, and a "Priority Tasks" card that showed the same
 * task again at the top of its list. One ordered queue says it once.
 *
 * It reads from `tasks` — the shared context array — and not from
 * `dashboard.priority_tasks`. That distinction fixes three bugs at once:
 * `useUpdateTask` optimistically updates the tasks cache but never invalidates
 * the dashboard one, so completing a task here used to leave it on screen; the
 * dashboard query is disabled on share links, where there is no projectId; and
 * its endpoint is owner-only, so collaborators 404 and saw an empty widget.
 *
 * The ordering is the whole value of the pane, so it is stated once, here, and
 * derived rather than stored.
 */

// In-progress outranks todo: "now" means what you are already holding.
const STATUS_RANK = { 'in-progress': 0, todo: 1 };
const DUE_RANK = { overdue: 0, today: 1, week: 2, later: 3, none: 4 };
const LEVEL_RANK = { high: 0, medium: 1, low: 2 };

const rank = (map, key, fallback) => map[key] ?? fallback;

function DueChip({ due }) {
    const formatted = formatDue(due);
    if (!formatted) return null;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                formatted.chip
            )}
        >
            <Calendar className="h-2.5 w-2.5" />
            {formatted.label}
        </span>
    );
}

function TaskRow({ task, readOnly, onComplete, onOpen, lead }) {
    return (
        <div
            className={cn(
                'group flex items-start gap-2.5 px-3 py-2 transition-colors',
                lead ? 'bg-accent/[0.07]' : 'hover:bg-white/[0.03]'
            )}
        >
            {readOnly ? (
                <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground/40" />
            ) : (
                <button
                    type="button"
                    onClick={() => onComplete(task.id)}
                    aria-label={`Complete ${task.title}`}
                    className="mt-0.5 flex-shrink-0 text-muted-foreground/50 transition-colors hover:text-green-500"
                >
                    {/* Two icons rather than a bare div, so the hover affordance is
                        the same shape as the state it produces. Only rendered when
                        it can actually be clicked — a hover preview on a disabled
                        control promises something that won't happen. */}
                    <Circle className="h-4 w-4 group-hover:hidden" />
                    <CheckCircle2 className="hidden h-4 w-4 group-hover:block" />
                </button>
            )}

            <button
                type="button"
                onClick={onOpen}
                className="min-w-0 flex-1 text-left"
            >
                <div className={cn('break-words text-sm', lead ? 'font-semibold' : 'font-medium')}>
                    <SmartText text={task.title} className="inline" />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {lead ? (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-accent">Focus</span>
                    ) : null}
                    <PriorityChip value={task.priority || 'medium'} />
                    <DueChip due={task.due_date} />
                    {task.status === 'in-progress' ? (
                        <span className="whitespace-nowrap rounded-full border border-accent/30 bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent">
                            Active
                        </span>
                    ) : null}
                </div>
            </button>
        </div>
    );
}

function NowPaneImpl({ tasks, readOnly, onComplete, onOpenBoard, onNewTask }) {
    const queue = useMemo(() => {
        const now = new Date();
        const open = [];
        // One pass rather than filter().map().sort() over the whole array —
        // this runs on every task mutation.
        for (const task of tasks) {
            if (task.status === 'done') continue;
            // Parked work is not "now" by definition — it is the one thing the
            // user has explicitly said they are not doing next.
            if (isDemoted(task)) continue;
            open.push({
                task,
                status: rank(STATUS_RANK, task.status, 2),
                due: rank(DUE_RANK, dueBucket(task.due_date, now), 4),
                priority: rank(LEVEL_RANK, task.priority, 1),
                importance: rank(LEVEL_RANK, task.importance, 1),
            });
        }
        open.sort((a, b) =>
            a.status - b.status ||
            a.due - b.due ||
            a.priority - b.priority ||
            a.importance - b.importance
        );
        return open.map((entry) => entry.task);
    }, [tasks]);

    return (
        <PaneShell
            icon={Target}
            label="Now"
            count={queue.length}
            accent="text-accent"
            action={<PaneLink onClick={onOpenBoard}>Board</PaneLink>}
        >
            {queue.length === 0 ? (
                <PaneEmpty
                    icon={CheckCircle2}
                    title="All clear"
                    hint={readOnly ? undefined : 'Nothing open on this project'}
                />
            ) : (
                <div className="cv-auto divide-y divide-white/[0.04]">
                    {queue.map((task, i) => (
                        <TaskRow
                            key={task.id}
                            task={task}
                            lead={i === 0}
                            readOnly={readOnly}
                            onComplete={onComplete}
                            onOpen={onOpenBoard}
                        />
                    ))}
                </div>
            )}

            {!readOnly ? (
                <button
                    type="button"
                    onClick={onNewTask}
                    className="mt-auto flex flex-shrink-0 items-center gap-2 border-t border-white/[0.06] px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-white/[0.03] hover:text-foreground"
                >
                    <Plus className="h-3.5 w-3.5" />
                    Add a task
                </button>
            ) : null}
        </PaneShell>
    );
}

export const NowPane = memo(NowPaneImpl);
export default NowPane;
